'use client';

import { IDiscount } from '@/app/[locale]/checkout/model/types';
import { ICartItem } from '@/entities/cart/model/types';
import { useCartStore } from '@/entities/cart/model/use-cart-store';
import {
  IGroupModifier,
  ISingleModifier,
} from '@/entities/modifier/model/types';
import { useComboProducts } from '@/entities/product/api/query';
import {
  IComboGroup,
  IProduct,
  ISavedModifier,
} from '@/entities/product/model/types';
import Counter from '@/entities/product/ui/counter';
import { NumberToPrice } from '@/features/number-to-price';
import { usePathname, useRouter } from '@/i18n/navigation';
import AddComment from '@/widgets/add-comment';
import ConfirmModal from '@/widgets/confirm-modal';
import TrashIcon from '@/widgets/map/Delivery/icons/TrashRed';
import { AspectRatio, Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { memo, useCallback, useEffect, useState } from 'react';
import QuestionIcon from '../assets/icons/question';
import { useCustomLocale } from '../hooks/use-custom-locale';
import { Button } from './button';

interface IProps {
  size?: string;
  data?: IProduct;
  price?: number;
  index?: number;
  title?: string;
  readOnly?: boolean;
  cartItem: ICartItem;
  bonus_product?: {
    is_bonus: boolean;
    product: IDiscount['bonus_product'];
  };
  customEvents?: boolean;
  setCommentCustomComment?: any;
  customRemove?: (e: ICartItem) => void;
  customIncrement?: (e: ICartItem) => void;
  customDecrement?: (e: ICartItem) => void;
}

function CardRow({
  data,
  index,
  title,
  price,
  readOnly,
  cartItem,
  bonus_product,
  customEvents,
  customRemove = () => {},
  customIncrement = () => {},
  customDecrement = () => {},
}: IProps) {
  const [isDialog, setDialog] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [variantsInfo, setVariantsInfo] = useState<any[]>([]);
  const [modifiersData, setModifiersData] = useState<any[]>([]);
  const [orderModifiers, setOrderModifiers] = useState<ISavedModifier[]>([]);
  const [modifiersPrice, setModifiersPrice] = useState<number>(0);
  const [isModifiersMounted, setModifiersMounted] = useState(false);
  const [comment, setComment] = useState({
    isOpen: false,
    comment: '',
  });

  const t = useTranslations();
  const router = useRouter();
  const currentLocale = useCustomLocale();
  const pathName = usePathname();
  const cart = useCartStore((state) => state.cart);
  const INCREMENT = useCartStore((state) => state.INCREMENT);
  const DECREMENT = useCartStore((state) => state.DECREMENT);
  const REMOVE = useCartStore((state) => state.REMOVE);
  const ADD_COMMENT = useCartStore((state) => state.ADD_COMMENT);
  const DELETE_COMMENT = useCartStore((state) => state.DELETE_COMMENT);

  const { data: comboData, error: comboError } = useComboProducts(
    data?.id || '',
    {
      menu_id: cart?.[0]?.menu_id,
    },
    {
      enabled:
        data?.type === 'combo' &&
        Object.keys(cartItem?.variants)?.length > 0 &&
        !!data?.id,
    }
  );

  const onIncrease = () => {
    if (customEvents) {
      customIncrement(cartItem);
    } else {
      const maxQty = cartItem?.max_qty || Infinity;

      if (cartItem?.quantity < maxQty) {
        INCREMENT(cartItem?.key);
      }
    }
  };

  // Memoize the decrement handler
  const onDecrease = useCallback(() => {
    if (customEvents) {
      customDecrement(cartItem);
    } else {
      const minQty = cartItem?.min_qty || 1;
      const currentQty = cartItem?.quantity;
      const isOptional = cartItem?.is_optional;

      if (currentQty > minQty) {
        DECREMENT(cartItem.key);
      } else {
        setDialog(isOptional !== undefined ? isOptional : true);
      }
    }
  }, [
    customEvents,
    customDecrement,
    cartItem?.key,
    cartItem?.quantity,
    cartItem?.min_qty,
    cartItem?.is_optional,
  ]);

  const onRemove = useCallback(() => {
    const willBeEmpty = cart?.length === 1;

    // Remove the item
    if (customEvents) {
      customRemove(cartItem);
    } else {
      REMOVE(cartItem?.key);
    }

    if (willBeEmpty && pathName?.includes('checkout')) {
      router.push('/');
    }
  }, [customEvents, customRemove, cartItem, router, cart?.length]);

  const getModifierInCart = useCallback(
    (modifierId: string) => {
      let result: ISavedModifier = {
        modifier_id: '',
        modifier_price: 0,
        modifier_quantity: 0,
        min_amount: 0,
        max_amount: 0,
        name: { en: '', ru: '', uz: '' },
        type: 'single',
      };

      cart?.forEach(
        (item) =>
          cartItem?.key == item.key &&
          item.order_modifiers.map((mod) =>
            mod.modifier_id === modifierId && mod.modifier_quantity > 0
              ? (result = mod)
              : mod
          )
      );
      return result;
    },
    [cart, cartItem]
  );

  // Combo variants
  useEffect(() => {
    if (comboData && cartItem?.variants) {
      // If products is combo, get info about variants
      const variantMap: any[] = [];
      comboData?.groups?.map((group: IComboGroup) => {
        if (cartItem?.variants?.[group?.id]) {
          group.variants.map((variant) => {
            if (variant.id === cartItem?.variants?.[group?.id]?.variant_id) {
              variantMap.push({ ...variant, group_id: group?.id });
            }
          });
        }
      });
      setVariantsInfo(variantMap);
    }
  }, [comboData, cartItem]);

  useEffect(() => {
    if (data?.modifiers) {
      const modifiersTitle: {
        id: string;
        title: { ru: string; uz: string; en: string };
        price: number;
        quantity: number;
      }[] = [];
      if (data?.modifiers?.single_modifiers) {
        const value = data?.modifiers?.single_modifiers;
        if (value) {
          value.map((mod: ISingleModifier) => {
            const modifierInCart: ISavedModifier | undefined =
              getModifierInCart(mod.id);
            if (
              modifierInCart?.modifier_id &&
              modifierInCart?.modifier_quantity > 0
            ) {
              modifiersTitle.push({
                id: mod.id,
                title: mod.name,
                price: mod.price,
                quantity: modifierInCart?.modifier_quantity,
              });
            }
          });
        }
      }
      if (data?.modifiers?.group_modifiers) {
        const value = data?.modifiers?.group_modifiers;

        if (value) {
          value.map((mod: IGroupModifier) =>
            mod?.variants?.map((variant) => {
              const modifierInCart: ISavedModifier = getModifierInCart(
                variant.id
              );
              if (
                modifierInCart?.modifier_id &&
                modifierInCart?.modifier_quantity > 0
              ) {
                modifiersTitle.push({
                  id: variant.id,
                  title: variant.title,
                  price: variant.out_price,
                  quantity: modifierInCart.modifier_quantity,
                });
              }
            })
          );
        }
      }
      setModifiersData(modifiersTitle);
    }
  }, [data?.modifiers, getModifierInCart]);

  useEffect(() => {
    if (orderModifiers?.length > 0) {
      let sum = 0;
      for (const modifier of orderModifiers) {
        sum += modifier.modifier_price * modifier.modifier_quantity;
      }
      setModifiersPrice(sum);
    }
  }, [orderModifiers]);

  useEffect(() => {
    if (cartItem.order_modifiers?.length > 0 && !isModifiersMounted) {
      setOrderModifiers(cartItem.order_modifiers);
      setModifiersMounted(true);
    }
  }, [cartItem.order_modifiers, isModifiersMounted]);

  useEffect(() => {
    if (comboError) {
      REMOVE(cartItem?.key);
    }
  }, [comboError, cartItem?.key]);

  const handleSaveComment = () => {
    ADD_COMMENT({ key: cartItem.key, comment: comment.comment });
    setComment((prevState) => ({
      ...prevState,
      isOpen: false,
      comment: '',
    }));
  };

  const renderText = () => {
    let fullText = '';

    if (variantsInfo.length > 0) {
      fullText += variantsInfo
        .map(
          (variant, idx) =>
            `${variant.title?.[currentLocale]} (${
              cartItem?.variants?.[idx]?.variant_id === variant.id
                ? cartItem?.variants?.[idx]?.quantity
                : ''
            } ${t('pcs')})`
        )
        .join(', ');
    }
    if (modifiersData?.length > 0) {
      fullText +=
        (fullText ? ', ' : '') +
        modifiersData
          .map(
            (modifier) =>
              `${modifier.title?.[currentLocale]} (${modifier.quantity} ${t(
                'pcs'
              )})`
          )
          .join(', ');
    }

    if (
      !data?.modifiers &&
      !variantsInfo.length &&
      data?.description?.[currentLocale]
    ) {
      fullText += (fullText ? ', ' : '') + data?.description[currentLocale];
    }
    return fullText;
  };

  const handleDeleteComment = () => {
    DELETE_COMMENT(cartItem.key);
    setComment((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };
  return (
    <Box mx={4}>
      <Flex
        gap={3}
        alignItems="center"
        justifyContent="space-between"
        position="relative"
      >
        <AspectRatio
          minW={{ base: '60px', lg: '64px' }}
          bg={'background'}
          ratio={1 / 1}
          overflow="hidden"
          rounded={16}
        >
          {data?.image ? (
            <Image
              src={`${process.env.BASE_URL}${data?.image}`}
              alt={`${data?.title?.[currentLocale]} - photo ${index}`}
              style={{ objectFit: 'cover' }}
              fill
            />
          ) : (
            <Box>
              <QuestionIcon />
            </Box>
          )}
        </AspectRatio>
        <VStack
          flex={1}
          gap="6px"
          justifyContent="start"
          alignItems="start"
          lineHeight={1.2}
          fontSize={{ base: 'sm', lg: 'md' }}
        >
          <Text color="text" fontWeight={{ base: 500, lg: 400 }}>
            {data?.title?.[currentLocale] || title}
          </Text>
          <Text
            color="gray.600"
            fontSize="xs"
            lineClamp={showFull ? undefined : 1}
            onClick={() => setShowFull(!showFull)}
          >
            {renderText()}
          </Text>

          {renderText()?.length > 80 && (
            <Text
              w="fit-content"
              fontSize="xs"
              cursor={'pointer'}
              fontWeight={400}
              _hover={{ color: 'primary' }}
              transform={`translateY(${showFull ? 0 : -5}px)`}
              onClick={() => setShowFull(!showFull)}
              mt={1}
            >
              {showFull ? t('show_less') : t('show_more')}
            </Text>
          )}
          <HStack fontWeight={400} gap="6px">
            <NumberToPrice
              value={
                price ? price : cartItem?.price_with_discount + modifiersPrice
              }
              textProps={{
                color: 'text',
                fontWeight: { base: 400, lg: 500 },
              }}
              spanProps={{
                color: 'text',
                fontWeight: { base: 400, lg: 500 },
                fontSize: { base: 'sm', lg: 'md' },
              }}
            />
            {data?.weight
              ? data?.weight > 0 && (
                  <Text
                    color="gray.600"
                    fontSize={{ base: 'md', lg: 'sm' }}
                    display={{ base: 'none', lg: 'block' }}
                  >
                    · {data?.weight} {data?.measurement}
                  </Text>
                )
              : null}
          </HStack>
        </VStack>

        <Box>
          {cartItem?.in_stop ? (
            <HStack alignItems="end" flexDir={'column'} gap={0}>
              <Button
                color={'red'}
                px={2}
                size="sm"
                variant={'outline'}
                colorPalette={'red'}
                rounded={8}
                onClick={onRemove}
              >
                <TrashIcon color="red" />
                {t('remove')}
              </Button>
              <Text fontSize={'xs'} color={'gray.700'}>
                {t('out_of_stock')}
              </Text>
            </HStack>
          ) : (
            <Counter
              height={{ base: '32px', lg: '48px' }}
              bg={'background'}
              variable={cartItem?.quantity}
              onDecrease={onDecrease}
              onIncrease={onIncrease}
              isDisabled={readOnly}
            />
          )}
        </Box>
      </Flex>
      <Box>
        {!cartItem?.default_product &&
          (!cartItem?.comment ? (
            <Text
              cursor="pointer"
              fontSize="sm"
              textAlign="end"
              color="blue.600"
              display={cartItem?.in_stop ? 'none' : 'block'}
              onClick={() => {
                if (!cartItem?.in_stop) {
                  setComment((prevState) => ({
                    ...prevState,
                    isOpen: true,
                  }));
                }
              }}
              opacity={cartItem?.in_stop ? 0.5 : 1}
            >
              {t('leave_comment')}
            </Text>
          ) : (
            <VStack
              p={4}
              mt={4}
              gap={4}
              w="full"
              rounded={16}
              fontSize="sm"
              color={'text'}
              bg="background"
            >
              <Flex
                w="full"
                fontWeight={500}
                alignItems="center"
                justifyContent="space-between"
              >
                <Text>{t('comment')}</Text>
                <Text
                  onClick={handleDeleteComment}
                  color="fg.error"
                  cursor="pointer"
                >
                  {t('remove')}
                </Text>
              </Flex>
              <Text w="full">{cartItem?.comment}</Text>
            </VStack>
          ))}
      </Box>
      <AddComment
        onOpenChange={() =>
          setComment((prevState) => ({
            ...prevState,
            isOpen: false,
          }))
        }
        setComment={setComment}
        comment={comment}
        handleSaveComment={handleSaveComment}
      />
      <ConfirmModal
        isOpen={isDialog}
        onConfirm={onRemove}
        title={t('attention')}
        onClose={() => setDialog(false)}
        description={t('are_you_sure-product')}
      />
    </Box>
  );
}

export default memo(CardRow);
